import { Stack } from "@mui/material";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { isNotNil } from "ramda-adjunct";
import { useFormat } from "@sinch/core/hooks/format";
import { IconStat } from "@sinch/components/IconStat/IconStat";
import { MdsCalculateXs, MdsDescriptionXs, MdsPaymentsXs } from "@sinch/icons";
import { CompanyHeaderFragment } from "./fragments.types";

/**
 * Finance and volume summary of a company: overdue obligations, everything invoiced so far and
 * the total number of orders. Each stat is omitted when the query did not select its data, so
 * consumers that do not need the totals can leave them out of the selection.
 */
export const CompanyStats: FC<{ company: CompanyHeaderFragment }> = ({ company }) => {
  const { num } = useFormat();
  const { t } = useTranslation();
  const { invoiceStatistics, orderCount } = company;

  const stats = [
    ...(invoiceStatistics
      ? [
          {
            icon: MdsPaymentsXs,
            value: num.currency(invoiceStatistics.totalDebtTaxAfterDueDate),
            title: t("CompanyHeader.obligationsAfterDueDate"),
          },
          {
            icon: MdsCalculateXs,
            value: num.currency(invoiceStatistics.totalPrice),
            title: t("CompanyHeader.invoicedOverall"),
          },
        ]
      : []),
    ...(isNotNil(orderCount)
      ? [
          {
            icon: MdsDescriptionXs,
            value: orderCount,
            title: t("CompanyHeader.orderCount"),
          },
        ]
      : []),
  ];

  if (stats.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: "nowrap", marginTop: 1, marginBottom: 0.5 }}>
      {stats.map((stat) => (
        <IconStat key={stat.title} icon={stat.icon} title={stat.title} value={stat.value} />
      ))}
    </Stack>
  );
};
